import React, { useCallback, useState } from 'react';
import styles from './SetUpProject.module.css';
import getCMResponse from '../../../utils/api_manager';
import shopifyIconAsset from '../../../assets/icons/shopify.svg';

const VALID_ROLES = ['OWNER', 'ADMIN', 'ANALYST', 'VIEWER'];

// Multi-Platform Business Support TRD, Bounded Unit B, Requirement B2 — the
// only two project types the allow-list (cq-api's validateProjectType)
// currently accepts. Adding a third type later is a cq-api allow-list edit
// plus one more entry in this array — no other change needed here.
const PROJECT_TYPES = [
  {
    id: 'SHOPIFY',
    name: 'Shopify',
    description:
      "A Shopify store is the core of this project. Unlocks Shopify-specific dashboards for orders, products, and store performance.",
  },
  {
    id: 'OTHER',
    name: 'Other',
    description:
      'No Shopify store. Connect Google Analytics, Ads, Search Console, Merchant Center, and Meta instead.',
  },
];

const isValidEmail = (email) => {
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const Icon = ({ name, size = 18, color = 'inherit', style = {} }) => (
  <span
    className="material-symbols-rounded"
    style={{ fontSize: size, color, lineHeight: 1, ...style }}
  >
    {name}
  </span>
);

/* ─── GreenField — styled text input ────────────────────────────── */
const GreenField = ({ label, children }) => (
  <div className={styles.fieldGroup}>
    <label className={styles.fieldLabel}>{label}</label>
    {children}
  </div>
);

/* ─── SetUpProject (Onboarding Step 1) ──────────────────────────────
 * Per the approved design (onboarding-step1/Onboarding Step 1.dc.html) and
 * Multi-Platform Business Support TRD Requirement B2:
 *   - project type is chosen here (Shopify / Other) and is IMMUTABLE once
 *     the project is created (PRD §8 Q21, cq-api AC-A2.3) — no edit
 *     affordance is rendered for it anywhere in this app, here or later.
 *   - the project record is created immediately on submit (POST /project
 *     with project_type + name), not deferred to a later step (PRD §8 Q23).
 *   - the Shopify store URL is no longer collected here — that moved to the
 *     Step 2 grant-access dialog (Requirement B3), Shopify-tile only.
 */
const SetUpProject = ({ onProjectCreated }) => {
  const [projectType, setProjectType] = useState('SHOPIFY');
  const [projectName, setProjectName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [typeInfoOpen, setTypeInfoOpen] = useState(false);

  // Focus state for the green-tinted name field
  const [nameFocused, setNameFocused] = useState(false);

  // Invite composer state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('ANALYST');
  const [roleOpen, setRoleOpen] = useState(false);
  const [invitees, setInvitees] = useState([]);

  const canAdd = isValidEmail(inviteEmail.trim());

  const handleAddInvitee = useCallback(() => {
    if (!canAdd) return;
    setInvitees((prev) => [
      ...prev,
      { email: inviteEmail.trim(), role: inviteRole },
    ]);
    setInviteEmail('');
  }, [canAdd, inviteEmail, inviteRole]);

  const handleRemoveInvitee = useCallback((idx) => {
    setInvitees((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const isValid = projectType && projectName.trim();

  const handleNext = useCallback(() => {
    setError('');
    if (!projectType) {
      setError('Project type is required.');
      return;
    }
    if (!projectName.trim()) {
      setError('Project name is required.');
      return;
    }

    setLoading(true);
    getCMResponse('/project', 'POST', {
      name: projectName.trim(),
      project_type: projectType,
      invitationList: invitees,
    })
      .then((response) => {
        setLoading(false);
        if (response.success && response.data) {
          if (onProjectCreated) onProjectCreated(response.data);
        } else if (
          response.error &&
          response.error.err_code === 'INVALID_PROJECT_TYPE'
        ) {
          setError('Invalid project type selected.');
        } else {
          setError(response.message || 'Failed to create project');
        }
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
        if (
          err.response?.data?.error &&
          err.response.data.error.err_code === 'INVALID_PROJECT_TYPE'
        ) {
          setError(err.response.data.message || 'Invalid project type selected.');
        } else {
          setError('Network or server error. Please try again.');
        }
      });
  }, [projectType, projectName, invitees, onProjectCreated]);

  return (
    <div className={styles.cardWrapper}>
      <div className={styles.card}>
        {/* Heading */}
        <h1 className={styles.heading}>Set up your project.</h1>
        <p className={styles.subtext}>
          Choose what you're connecting, then give it a name. You'll connect
          data sources in the next step.
        </p>

        {/* Fields */}
        <div className={styles.fields}>
          {/* Project type selector */}
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>
              Project type{' '}
              <span
                className={styles.infoIconWrapper}
                onMouseEnter={() => setTypeInfoOpen(true)}
                onMouseLeave={() => setTypeInfoOpen(false)}
              >
                <button
                  type="button"
                  className={styles.infoIconBtn}
                  aria-label="What is project type?"
                  onFocus={() => setTypeInfoOpen(true)}
                  onBlur={() => setTypeInfoOpen(false)}
                >
                  <Icon name="info" size={14} color="var(--color-on-surface-muted)" />
                </button>
                {typeInfoOpen && (
                  <div className={styles.tooltip} role="tooltip">
                    <strong>Project type</strong> sets which connectors and
                    dashboards are available to this project — for example, a
                    Shopify project unlocks Shopify-specific dashboards. It's
                    a one-time choice made now and can't be changed later.
                  </div>
                )}
              </span>
            </label>

            <div className={styles.typeGrid}>
              {PROJECT_TYPES.map((type) => {
                const selected = projectType === type.id;
                return (
                  <button
                    type="button"
                    key={type.id}
                    className={`${styles.typeCard} ${selected ? styles.typeCardSelected : ''}`}
                    onClick={() => setProjectType(type.id)}
                    disabled={loading}
                    aria-pressed={selected}
                  >
                    <div className={styles.typeCardTop}>
                      <div className={styles.typeIcon}>
                        {type.id === 'SHOPIFY' ? (
                          <img src={shopifyIconAsset} alt="" width={22} height={22} />
                        ) : (
                          <Icon name="apps" size={22} color="var(--color-primary)" />
                        )}
                      </div>
                      <span
                        className={`${styles.typeRadio} ${selected ? styles.typeRadioSelected : ''}`}
                        aria-hidden="true"
                      />
                    </div>
                    <div className={styles.typeName}>{type.name}</div>
                    <p className={styles.typeDesc}>{type.description}</p>
                  </button>
                );
              })}
            </div>

            <p className={styles.lockNotice}>
              <Icon name="lock" size={13} color="#b98900" style={{ marginRight: 4, verticalAlign: '-2px' }} />
              <strong>This choice is permanent</strong> — project type can't
              be changed after setup, since it determines which connectors
              and dashboards are available.
            </p>
          </div>

          {/* Project name */}
          <GreenField label="Project name">
            <div
              className={styles.inputWrapper}
              style={{
                background: nameFocused ? '#fff' : '#e9f3f0',
                boxShadow: nameFocused
                  ? 'inset 0 0 0 1px var(--color-primary), var(--shadow-focus)'
                  : 'none',
              }}
            >
              <input
                type="text"
                className={styles.textInput}
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                onFocus={() => setNameFocused(true)}
                onBlur={() => setNameFocused(false)}
                placeholder="My Shopify Store"
                disabled={loading}
                autoComplete="off"
              />
            </div>
          </GreenField>

          {/* Add teammates */}
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>
              Add teammates{' '}
              <span className={styles.optional}>(optional)</span>
            </label>

            {/* Composer row */}
            <div className={styles.composerRow}>
              {/* @ icon */}
              <div className={styles.composerIcon}>
                <Icon name="alternate_email" size={16} color="var(--color-primary)" />
              </div>

              {/* Email input */}
              <input
                type="email"
                className={styles.composerEmail}
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAddInvitee();
                }}
                placeholder="colleague@company.com"
                disabled={loading}
              />

              {/* Role selector */}
              <div className={styles.roleWrapper}>
                <button
                  type="button"
                  className={styles.roleBtn}
                  onClick={() => setRoleOpen((o) => !o)}
                  disabled={loading}
                >
                  {inviteRole}
                  <Icon name="expand_more" size={14} color="var(--color-primary)" />
                </button>
                {roleOpen && (
                  <div className={styles.roleDropdown}>
                    {VALID_ROLES.map((r) => (
                      <button
                        key={r}
                        type="button"
                        className={`${styles.roleOption} ${r === inviteRole ? styles.roleOptionActive : ''}`}
                        onClick={() => {
                          setInviteRole(r);
                          setRoleOpen(false);
                        }}
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Add button */}
              <button
                type="button"
                className={styles.addBtn}
                onClick={handleAddInvitee}
                disabled={!canAdd || loading}
                aria-label="Add teammate"
                style={{ opacity: canAdd ? 1 : 0.4 }}
              >
                <Icon name="arrow_forward" size={16} color="#fff" />
              </button>
            </div>

            {/* Chips */}
            {invitees.length > 0 && (
              <div className={styles.chips}>
                {invitees.map((inv, idx) => (
                  <div key={idx} className={styles.chip}>
                    <span className={styles.chipEmail}>{inv.email}</span>
                    <span className={styles.chipRole}>{inv.role}</span>
                    <button
                      type="button"
                      className={styles.chipRemove}
                      onClick={() => handleRemoveInvitee(idx)}
                      aria-label={`Remove ${inv.email}`}
                    >
                      <Icon name="close" size={13} color="var(--color-on-surface-muted)" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className={styles.errorBanner}>
            <Icon name="error" size={16} color="var(--color-error)" />
            <span>{error}</span>
          </div>
        )}

        {/* Footer */}
        <div className={styles.footer}>
          <span className={styles.footerNote}>
            You can change most of this later
          </span>
          <button
            type="button"
            className={styles.continueBtn}
            onClick={handleNext}
            disabled={loading || !isValid}
            style={{ opacity: isValid && !loading ? 1 : 0.4 }}
          >
            {loading ? (
              'Creating project…'
            ) : (
              <>
                Continue
                <Icon name="arrow_forward" size={16} color="#fff" style={{ marginLeft: 6 }} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SetUpProject;
